import { useReducer, useEffect, useCallback, useMemo } from 'react';
import { useCatalogService } from '../services/catalogService';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';
import type { Deployment } from '../../../types/backend';
import type { ParameterSource, PreviewResolutionResponse, SetDesiredParametersResponse } from '../types';

interface State {
  values: Record<string, string>;
  errors: Record<string, string>;
  submitting: boolean;
  preview: PreviewResolutionResponse | null;
  previewLoading: boolean;
  previewUnavailable: boolean;
}

type Action =
  | { type: 'SET_VALUE'; key: string; value: string }
  | { type: 'SET_ERRORS'; errors: Record<string, string> }
  | { type: 'SUBMITTING'; submitting: boolean }
  | { type: 'SET_PREVIEW'; preview: PreviewResolutionResponse }
  | { type: 'PREVIEW_UNAVAILABLE' }
  | { type: 'RESET'; inputKeys: string[] };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_VALUE':
      return {
        ...state,
        values: { ...state.values, [action.key]: action.value },
        errors: { ...state.errors, [action.key]: '' },
      };
    case 'SET_ERRORS':
      return { ...state, errors: action.errors };
    case 'SUBMITTING':
      return { ...state, submitting: action.submitting };
    case 'SET_PREVIEW':
      return { ...state, preview: action.preview, previewLoading: false, previewUnavailable: false };
    case 'PREVIEW_UNAVAILABLE':
      return { ...state, previewLoading: false, previewUnavailable: true };
    case 'RESET': {
      const values: Record<string, string> = {};
      action.inputKeys.forEach((k) => { values[k] = ''; });
      return { ...state, values, errors: {}, submitting: false };
    }
    default:
      return state;
  }
}

function initialState(): State {
  return {
    values: {},
    errors: {},
    submitting: false,
    preview: null,
    previewLoading: true,
    previewUnavailable: false,
  };
}

interface UseDeploymentParametersOptions {
  customerId: string;
  deployment: Deployment;
  onSuccess: () => void;
}

export function useDeploymentParameters({
  customerId,
  deployment,
  onSuccess,
}: UseDeploymentParametersOptions) {
  const catalogService = useCatalogService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [state, dispatch] = useReducer(reducer, undefined, initialState);

  const inputKeys = useMemo(
    () => Object.entries(state.preview?.sources ?? {})
      .filter(([, src]) => (src as ParameterSource) === 'operator-input')
      .map(([key]) => key),
    [state.preview],
  );

  useEffect(() => {
    dispatch({ type: 'RESET', inputKeys });
  }, [deployment.id, inputKeys]);

  useEffect(() => {
    let mounted = true;
    catalogService
      .previewResolution(customerId, deployment.account_id, deployment.product_id, deployment.service, deployment.id)
      .then((result) => {
        if (mounted) {
          dispatch({ type: 'SET_PREVIEW', preview: result });
        }
      })
      .catch(() => {
        if (mounted) dispatch({ type: 'PREVIEW_UNAVAILABLE' });
      });
    return () => { mounted = false; };
  }, [customerId, deployment.account_id, deployment.product_id, deployment.service, deployment.id, catalogService]);

  const setValue = useCallback((key: string, value: string) => {
    dispatch({ type: 'SET_VALUE', key, value });
  }, []);

  const clearValue = useCallback((key: string) => {
    dispatch({ type: 'SET_VALUE', key, value: '' });
  }, []);

  const submit = useCallback(async (): Promise<boolean> => {
    const errors: Record<string, string> = {};
    for (const key of inputKeys) {
      if (!state.values[key]?.trim()) {
        errors[key] = 'Required';
      }
    }
    if (Object.keys(errors).length > 0) {
      dispatch({ type: 'SET_ERRORS', errors });
      return false;
    }

    dispatch({ type: 'SUBMITTING', submitting: true });
    try {
      const payload: Record<string, string | null> = {};
      for (const key of inputKeys) {
        const trimmed = state.values[key]?.trim();
        payload[key] = trimmed || null;
      }
      const result = (await catalogService.setDesiredParameters(
        customerId,
        deployment.id,
        payload,
      )) as SetDesiredParametersResponse;

      if (result.unresolved.length === 0) {
        showSnackbar('Parameters saved — deployment will reconcile shortly');
        onSuccess();
        return true;
      }

      const remaining: Record<string, string> = {};
      result.unresolved.forEach((k) => {
        remaining[k] = 'Still required';
      });
      dispatch({ type: 'SET_ERRORS', errors: remaining });
      showSnackbar(`Still unresolved: ${result.unresolved.join(', ')}`, 'warning');
      return false;
    } catch (err) {
      showSnackbar(extractErrorMessage(err, 'Failed to save parameters'), 'error');
      return false;
    } finally {
      dispatch({ type: 'SUBMITTING', submitting: false });
    }
  }, [inputKeys, state.values, catalogService, customerId, deployment.id, showSnackbar, onSuccess]);

  return {
    values: state.values,
    errors: state.errors,
    submitting: state.submitting,
    preview: state.preview,
    previewLoading: state.previewLoading,
    previewUnavailable: state.previewUnavailable,
    setValue,
    clearValue,
    submit,
    snackbar,
    closeSnackbar,
  };
}
